import { api } from "./client";

export interface AssetSearchResult {
  symbol: string;
  display_symbol?: string | null;
  name: string;
  currency: string;
  asset_class: string;
  asset_sub_class: string;
  data_source: string;
  logo?: string | null;
}

export interface SymbolProfileCreate {
  symbol: string;
  name?: string | null;
  currency?: string | null;
  data_source: string;
  asset_class?: string | null;
  asset_sub_class?: string | null;
}

export interface SymbolProfileRead {
  id: string;
  symbol: string;
  display_symbol?: string | null;
  name: string | null;
  currency: string | null;
  data_source: string;
  asset_class: string | null;
  asset_sub_class: string | null;
  is_active: boolean;
}

export interface PortfolioPosition {
  symbol: string;
  display_symbol?: string | null;
  name: string | null;
  currency: string | null;
  data_source: string;
  asset_class: string | null;
  asset_sub_class: string | null;
  quantity: number;
  average_price: number;
  market_price: number;
  value_in_base_currency: number;
  investment: number;
  allocation_in_percentage: number;
  gross_performance: number;
  gross_performance_percent: number;
  net_performance: number;
  net_performance_percent: number;
  net_performance_percent_with_currency_effect: number;
  net_performance_with_currency_effect: number;
  dividend: number;
  date_of_first_activity: string;
  activities_count: number;
  countries: any[];
  sectors: any[];
  account_names: string[];
  tags: any[];
}

export const portfolioApi = {
  searchAssets: async (query: string, category?: string): Promise<AssetSearchResult[]> => {
    const { data } = await api.get(`/api/portfolio/search`, {
      params: { query, category },
    });
    return data;
  },
  createAsset: async (data: SymbolProfileCreate): Promise<SymbolProfileRead> => {
    const response = await api.post(`/api/portfolio/assets`, data);
    return response.data;
  },
  getHoldings: async (): Promise<PortfolioPosition[]> => {
    const response = await api.get(`/api/portfolio/holdings`);
    return response.data;
  },
  getAssets: async (): Promise<SymbolProfileRead[]> => {
    const response = await api.get(`/api/portfolio/assets`);
    return response.data;
  },
  getMarketPrice: async (symbol: string, date: string): Promise<{ price: number } | null> => {
    try {
      const response = await api.get(`/api/portfolio/market-price`, {
        params: { symbol, date },
      });
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 404) return null;
      throw e;
    }
  },
  getNetWorth: async (): Promise<NetWorthResponse> => {
    const response = await api.get(`/api/portfolio/net-worth`);
    return response.data;
  },
  getGoalsProjection: async (): Promise<GoalProjectionPoint[]> => {
    const response = await api.get(`/api/portfolio/goals-projection`);
    return response.data;
  },
  getAssetPerformance: async (dataSource: string, symbol: string): Promise<AssetPerformanceResponse> => {
    const response = await api.get(`/api/portfolio/assets/${dataSource}/${symbol}/performance`);
    return response.data;
  },
  getAnalysis: async (): Promise<PortfolioAnalysisResponse> => {
    const response = await api.get(`/api/portfolio/analysis`);
    return response.data;
  },
};

export interface DividendTimelineEvent {
  date: string;
  symbol: string;
  display_symbol?: string | null;
  amount_in_base_currency: number;
}

export interface PortfolioAnalysisResponse {
  base_currency: string;
  estimated_annual_dividends: number;
  dividends_timeline: DividendTimelineEvent[];
}

export interface NetWorthPoint {
  date: string;
  value: number;
  liquid: number;
  investments: number;
  properties?: number;
  portfolio_investment: number;
  portfolio_return_percent: number;
}

export interface NetWorthResponse {
  current_net_worth: number;
  yoy_change_percent: number | null;
  current_portfolio_value: number;
  portfolio_yoy_change_percent: number | null;
  history: NetWorthPoint[];
}

export interface GoalProjectionPoint {
  year: number;
  value: number;
  properties: number;
}

export interface AssetPerformancePoint {
  date: string;
  value: number;
  invested: number;
  net_return: number;
  return_percent: number;
}

export interface AssetDividendModel {
  id: string;
  date: string;
  amount: number;
  amount_in_base: number;
  amount_per_share: number;
  currency: string;
  status: "CONFIRMED" | "PENDING";
  source: "YAHOO" | "MANUAL";
  is_deleted?: boolean;
}

export const dividendApi = {
  list: async (symbol: string, dataSource: string): Promise<AssetDividendModel[]> => {
    const response = await api.get(`/api/portfolio/dividends`, {
      params: { symbol, data_source: dataSource },
    });
    return response.data;
  },
  create: async (data: {
    symbol: string;
    data_source: string;
    ex_date: string;
    amount_per_share: number;
    currency: string;
  }): Promise<AssetDividendModel> => {
    const response = await api.post(`/api/portfolio/dividends`, bodyToCreate(data));
    return response.data;
  },
  update: async (
    id: string,
    data: {
      ex_date?: string;
      amount_per_share?: number;
      currency?: string;
      withholding_tax?: number | null;
    }
  ): Promise<AssetDividendModel> => {
    const response = await api.put(`/api/portfolio/dividends/${id}`, bodyToUpdate(data));
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/api/portfolio/dividends/${id}`);
  },
  recover: async (id: string): Promise<AssetDividendModel> => {
    const response = await api.post(`/api/portfolio/dividends/${id}/recover`);
    return response.data;
  },
  sync: async (symbol: string, dataSource: string): Promise<{ status: string }> => {
    const response = await api.post(`/api/portfolio/dividends/sync`, null, {
      params: { symbol, data_source: dataSource },
    });
    return response.data;
  },
};

function bodyToCreate(data: any) {
  return {
    symbol: data.symbol,
    data_source: data.data_source,
    ex_date: data.ex_date,
    amount_per_share: data.amount_per_share,
    currency: data.currency,
  };
}

function bodyToUpdate(data: any) {
  const payload: any = {};
  if (data.ex_date !== undefined) payload.ex_date = data.ex_date;
  if (data.amount_per_share !== undefined) payload.amount_per_share = data.amount_per_share;
  if (data.currency !== undefined) payload.currency = data.currency;
  if (data.withholding_tax !== undefined) payload.withholding_tax = data.withholding_tax;
  return payload;
}


export interface AssetActivityModel {
  id: string;
  date: string;
  type: string;
  quantity: number;
  unit_price: number;
  fee: number;
  currency: string;
  summary: string;
}

export interface AssetPerformanceResponse {
  symbol: string;
  display_symbol?: string | null;
  name: string | null;
  currency: string | null;
  asset_class?: string | null;
  asset_sub_class?: string | null;
  base_currency: string;
  market_price?: number;
  market_price_in_base?: number;
  current_value: number;
  current_value_in_base: number;
  average_price: number;
  average_price_in_base: number;
  total_dividends: number;
  total_dividends_in_base: number;
  gross_performance: number;
  gross_performance_in_base: number;
  net_performance: number;
  net_performance_in_base: number;
  net_performance_percent: number;
  history: AssetPerformancePoint[];
  dividends: AssetDividendModel[];
  activities: AssetActivityModel[];
}

